import React, {useState} from 'react';
import { Box, Typography, Grid, Card, CardContent, LinearProgress } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend } from 'recharts';
import KazakhstanMap from "../../../Map";
import {kazakhstanRegions} from "../../../../../model/regions";
import { useTranslation } from 'react-i18next';



const CompanyAnalytics = () => {
    const { t } = useTranslation();

    const getCountryData = (t: any) => [
        { name: t('investment_analytics.countries.china'), value: 24.4 },
        { name: t('investment_analytics.countries.usa'), value: 21.7 },
        { name: t('investment_analytics.countries.states'), value: 17.9 },
        { name: t('investment_analytics.countries.netherlands'), value: 16.2 },
        { name: t('investment_analytics.countries.turkey'), value: 14.7 },
        { name: t('investment_analytics.countries.southKorea'), value: 13 },
        { name: t('investment_analytics.countries.uzbekistan'), value: 10.6 },
        { name: t('investment_analytics.countries.greatBritain'), value: 9.9 },
        { name: t('investment_analytics.countries.switzerland'), value: 9.1 },
        { name: t('investment_analytics.countries.ukraine'), value: 8.5 }
    ];

    const getSuppliersData = (t: any) => [
        { name: 'ZhanaTLM LLP', value: 32.9 },
        { name: 'Alyntys.kz', value: 31.7 },
        { name: 'BI Group', value: 27.9 },
        { name: t('investment_analytics.suppliers.altynZol'), value: 22.7 }
    ];

    const getTenderStats = (t: any) => [
        { month: t('investment_analytics.months.jan'), tenders: 10, contracts: 50 },
        { month: t('investment_analytics.months.aug'), tenders: 40, contracts: 150 },
        { month: t('investment_analytics.months.oct'), tenders: 80, contracts: 300 },
        { month: t('investment_analytics.months.oct'), tenders: 110, contracts: 400 },
        { month: t('investment_analytics.months.oct'), tenders: 125, contracts: 495 }
    ];

    const getRegionData = (t: any) => [
        { name: t('investment_analytics.regions.south'), value: 200 },
        { name: t('investment_analytics.regions.north'), value: 180 },
        { name: t('investment_analytics.regions.center'), value: 160 },
        { name: t('investment_analytics.regions.east'), value: 140 },
        { name: t('investment_analytics.regions.west'), value: 120 }
    ];
    const countryData = getCountryData(t);
    const suppliersData = getSuppliersData(t);
    const tenderStats = getTenderStats(t);
    const regionData = getRegionData(t);

    const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
    const [hoveredRegion, setHoveredRegion] = useState<string | null>(null);


    const handleRegionClick = (region: any) => {
        setSelectedRegion(region.name);
        console.log(t('investment_analytics.logs.regionSelected'), region);
    };

    const handleRegionHover = (region: any) => {
        setHoveredRegion(region ? region.name : null);
    };
    return (
        <Box p={4}>
            <Typography fontWeight={700}
                        variant={'h4'} gutterBottom>
                {t('investment_analytics.title')}
            </Typography>
            <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography   variant={'h6'}
                                          gutterBottom
                                          sx={{fontWeight: 700}}>{t('investment_analytics.sections.investmentActivity')}</Typography>
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={tenderStats}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="month" />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Line type="monotone" dataKey="tenders" stroke="#1976d2" name={t('investment_analytics.charts.tenderCount')} />
                                    <Line type="monotone" dataKey="contracts" stroke="#90caf9" name={t('investment_analytics.charts.contractVolume')} />
                                </LineChart>
                            </ResponsiveContainer>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography   variant={'h6'}
                                          gutterBottom
                                          sx={{fontWeight: 700}}>{t('investment_analytics.sections.topCountries')}</Typography>
                            {countryData.map((item) => (
                                <Box key={item.name} display="flex" alignItems="center" my={1}>
                                    <Typography width={120}>{item.name}</Typography>
                                    <LinearProgress variant="determinate" value={(item.value / 24.4) * 100} sx={{ flexGrow: 1, mx: 2 }} />
                                    <Typography>{item.value} M</Typography>
                                </Box>
                            ))}
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card>
                        <CardContent>
                            <Typography   variant={'h6'}
                                          gutterBottom
                                          sx={{fontWeight: 700}}>{t('investment_analytics.sections.keyIndicators')}</Typography>
                            <Typography>📌 {t('investment_analytics.indicators.tenders')}: <strong>125</strong></Typography>
                            <Typography>💰 {t('investment_analytics.indicators.contractVolume')}: <strong>$495M</strong></Typography>
                            <Typography>🏢 {t('investment_analytics.indicators.localSuppliers')}: <strong>60%</strong></Typography>
                            <Typography>✅ {t('investment_analytics.indicators.accreditedSuppliers')}: <strong>410</strong></Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={8}>
                    <Card>
                        <CardContent>
                            <Typography   variant={'h6'}
                                          gutterBottom
                                          sx={{fontWeight: 700}}>{t('investment_analytics.sections.supplierAnalysis')}</Typography>
                            {suppliersData.map((item) => (
                                <Box key={item.name} display="flex" alignItems="center" my={1}>
                                    <Typography width={150}>{item.name}</Typography>
                                    <LinearProgress variant="determinate" value={(item.value / 32.9) * 100} sx={{ flexGrow: 1, mx: 2 }} />
                                    <Typography>{item.value} M</Typography>
                                </Box>
                            ))}
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography   variant={'h6'}
                                          gutterBottom
                                          sx={{fontWeight: 700}}>{t('investment_analytics.sections.regionDemandMap')}</Typography>
                            <Box sx={{ width: '100%', textAlign: 'center' }}>
                                <KazakhstanMap
                                    regions={kazakhstanRegions}
                                    onRegionClick={handleRegionClick}
                                    onRegionHover={handleRegionHover}
                                    defaultFill="#5764f7"
                                    hoverFill="#4254f5"
                                    activeFill="#3244d0"
                                />
                            </Box>
                            <Typography variant="body2" mt={2}>
                                * {t('investment_analytics.descriptions.mapDescription')}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography   variant={'h6'}
                                          gutterBottom
                                          sx={{fontWeight: 700}}>{t('investment_analytics.sections.regionDemandChart')}</Typography>
                            <ResponsiveContainer width="100%" height={250}>
                                <BarChart data={regionData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" />
                                    <YAxis />
                                    <Tooltip />
                                    <Bar dataKey="value" fill="#42a5f5" />
                                </BarChart>
                            </ResponsiveContainer>
                            <Typography variant="body2" mt={2}>
                                * {t('investment_analytics.descriptions.chartDescription')}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default CompanyAnalytics;
